import { GetRequest, PostData } from "./FetchData";

const MS1 = "ms1";
const BASE = "api/v1/website";

export const SCHEMA_OPTIMIZER_JOB_POLL_MS = 2500;
export const SCHEMA_OPTIMIZER_LIST_POLL_MS = 3000;

const IN_FLIGHT = new Set([
	"PENDING",
	"SCRAPING",
	"CLASSIFYING",
	"GENERATING",
	"BATCH_SUBMITTED",
	"BATCH_POLLING",
	"VALIDATING",
]);

function extractMs1Message(payload, fallbackMessage) {
	if (typeof payload === "string" && payload.trim()) return payload;
	if (payload && typeof payload.message === "string" && payload.message.trim()) {
		return payload.message;
	}
	if (payload?.message && typeof payload.message.message === "string") {
		return payload.message.message;
	}
	if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
		return payload.detail;
	}
	return fallbackMessage;
}

function parseMs1Response(res, fallbackMessage = "Request failed") {
	if (res === "Invalid" || res === undefined) {
		return { ok: false, message: fallbackMessage, data: null };
	}
	if (res.status === "failure") {
		return {
			ok: false,
			message: extractMs1Message(res, fallbackMessage),
			data: res.data ?? null,
		};
	}
	if (
		res.status === "not_found_error" ||
		res.status === "api_error" ||
		res.status === "server_error" ||
		res.status === "application_error"
	) {
		return {
			ok: false,
			message: extractMs1Message(res.message ?? res, fallbackMessage),
			data: res.data ?? null,
		};
	}
	if (res?.status !== "success") {
		return {
			ok: false,
			message: extractMs1Message(res?.message ?? res, fallbackMessage),
			data: null,
		};
	}
	return { ok: true, message: res.message ?? null, data: res.data ?? null };
}

function schemaWebsiteId(websiteId) {
	const n = Number(websiteId);
	return Number.isFinite(n) ? n : websiteId;
}

function normalizeSchemaJobId(jobId) {
	const n = Number(jobId);
	return Number.isFinite(n) ? n : String(jobId);
}

function requireWebsiteId(websiteId) {
	if (websiteId == null || websiteId === "") {
		return { ok: false, message: "Missing website id", data: null };
	}
	return null;
}

function schemaParams(params) {
	const out = {};
	Object.entries(params).forEach(([key, value]) => {
		if (value == null || value === "") return;
		if (key === "websiteId") out[key] = schemaWebsiteId(value);
		else if (key === "jobId") out[key] = normalizeSchemaJobId(value);
		else out[key] = value;
	});
	return out;
}

function schemaQuery(params) {
	const qs = new URLSearchParams();
	Object.entries(schemaParams(params)).forEach(([key, value]) => {
		qs.set(key, String(value));
	});
	return qs.toString();
}

async function postSchema(path, body, fallbackMessage) {
	const res = await PostData(MS1, `${BASE}/${path}`, body);
	return parseMs1Response(res, fallbackMessage);
}

async function getSchema(path, params, fallbackMessage) {
	const query = schemaQuery(params);
	const res = await GetRequest(MS1, `${BASE}/${path}?${query}`);
	return parseMs1Response(res, fallbackMessage);
}

/** POST + JSON body (same as other schema-optimizer MS1 routes). */
async function deleteSchema(path, params, fallbackMessage) {
	return postSchema(path, schemaParams(params), fallbackMessage);
}

export function isSchemaOptimizerJobInFlight(status) {
	return IN_FLIGHT.has(String(status || "").toUpperCase());
}

export function isSchemaOptimizerJobSuccess(status) {
	return String(status || "").toUpperCase() === "SUCCESS";
}

export function isSchemaOptimizerJobFailed(status) {
	return String(status || "").toUpperCase() === "FAILURE";
}

export function isSchemaOptimizerRunInFlight(status) {
	const s = String(status || "").toUpperCase();
	return s === "PENDING" || s === "RUNNING";
}

function normalizePreview(data) {
	if (!data) return null;
	return {
		urls: Array.isArray(data.urls) ? data.urls : [],
		totalFound: Number(data.totalFound ?? data.total_found ?? 0),
		truncated: Boolean(data.truncated),
		skipped: Array.isArray(data.skipped) ? data.skipped : [],
	};
}

function normalizeSubmitJob(data) {
	if (!data) return null;
	return {
		jobId: Number(data.jobId ?? data.job_id),
		taskId: data.taskId ?? data.task_id ?? null,
	};
}

function normalizeSubmitRun(data) {
	if (!data) return null;
	const jobIds = data.jobIds ?? data.job_ids;
	const runId = data.runId ?? data.run_id ?? data.id;
	const runIdNum = Number(runId);
	return {
		runId: Number.isFinite(runIdNum) ? runIdNum : runId,
		taskId: data.taskId ?? data.task_id ?? null,
		jobIds: Array.isArray(jobIds) ? jobIds.map((id) => Number(id)) : [],
	};
}

function normalizeRunDetail(data) {
	if (!data || typeof data !== "object") return null;
	return {
		...data,
		id: data.id ?? data.runId ?? data.run_id,
		source: data.source,
		status: data.status,
		progressMessage: data.progressMessage ?? data.progress_message ?? null,
		useBatchApi: data.useBatchApi ?? data.use_batch_api,
		jobs: Array.isArray(data.jobs) ? data.jobs : [],
	};
}

export async function submitSchemaOptimizerJob(
	websiteId,
	{ url, content, domainUrl, useBatchApi = false, sessionId } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, job: null };
	const body = {
		websiteId: schemaWebsiteId(websiteId),
		useBatchApi: Boolean(useBatchApi),
	};
	if (url) body.url = String(url).trim();
	if (content) body.content = String(content);
	if (domainUrl) body.domainUrl = String(domainUrl).trim();
	if (sessionId) body.sessionId = sessionId;
	const parsed = await postSchema(
		"schemaOptimizerSubmitJob",
		body,
		"Failed to submit schema job"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, job: null };
	const job = normalizeSubmitJob(parsed.data);
	if (!job?.jobId) {
		return { ok: false, message: "No job id returned", job: null };
	}
	return { ok: true, message: parsed.message, job };
}

export async function getSchemaOptimizerJob(websiteId, jobId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, job: null };
	if (jobId == null || jobId === "") {
		return { ok: false, message: "Missing job id", job: null };
	}
	const parsed = await getSchema(
		"schemaOptimizerJob",
		{ websiteId, jobId },
		"Failed to load schema job"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, job: null };
	return { ok: true, message: parsed.message, job: parsed.data };
}

export async function getSchemaOptimizerLatestJob(websiteId, url) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, job: null };
	if (!url) return { ok: false, message: "Missing url", job: null };
	const parsed = await getSchema(
		"schemaOptimizerLatestJob",
		{ websiteId, url: String(url) },
		"Failed to load latest schema job"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, job: null };
	return { ok: true, message: parsed.message, job: parsed.data };
}

export async function listSchemaOptimizerJobHistory(
	websiteId,
	url,
	{ skip = 0, limit = 20 } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) {
		return { ...missing, url: null, jobs: [], total: 0, hasMore: false };
	}
	const parsed = await getSchema(
		"schemaOptimizerJobHistory",
		{
			websiteId,
			url: String(url),
			skip: Number(skip),
			limit: Number(limit),
		},
		"Failed to load job history"
	);
	if (!parsed.ok) {
		return {
			ok: false,
			message: parsed.message,
			url: null,
			jobs: [],
			total: 0,
			hasMore: false,
		};
	}
	const d = parsed.data || {};
	return {
		ok: true,
		message: parsed.message,
		url: d.url ?? url,
		jobs: Array.isArray(d.jobs) ? d.jobs : [],
		total: Number(d.total ?? 0),
		skip: Number(d.skip ?? skip),
		limit: Number(d.limit ?? limit),
		hasMore: Boolean(d.hasMore ?? d.has_more),
	};
}

export async function listSchemaOptimizerJobs(
	websiteId,
	{ skip = 0, limit = 20, q = "" } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) {
		return { ...missing, jobs: [], total: 0, hasMore: false, skip, limit };
	}
	const params = {
		websiteId,
		skip: Number(skip),
		limit: Number(limit),
	};
	const query = String(q || "").trim();
	if (query) params.q = query;
	const parsed = await getSchema("schemaOptimizerJobs", params, "Failed to load schema jobs");
	if (!parsed.ok) {
		return {
			ok: false,
			message: parsed.message,
			jobs: [],
			total: 0,
			hasMore: false,
			skip,
			limit,
		};
	}
	const d = parsed.data || {};
	const jobs = Array.isArray(d.jobs) ? d.jobs : [];
	return {
		ok: true,
		message: parsed.message,
		jobs,
		total: Number(d.total ?? jobs.length),
		skip: Number(d.skip ?? skip),
		limit: Number(d.limit ?? limit),
		hasMore: Boolean(d.hasMore ?? d.has_more),
	};
}

export async function deleteSchemaOptimizerJob(websiteId, jobId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	if (jobId == null || jobId === "") {
		return { ok: false, message: "Missing job id", data: null };
	}
	return deleteSchema(
		"schemaOptimizerDeleteJob",
		{ websiteId, jobId: normalizeSchemaJobId(jobId) },
		"Failed to delete job"
	);
}

/**
 * POST api/v1/website/schemaOptimizerDeleteJobsByUrl — body: { websiteId, url }.
 * Use the exact `url` string from the jobs list.
 */
export async function deleteSchemaOptimizerJobsByUrl(websiteId, pageUrl) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;

	const url = String(pageUrl ?? "").trim();
	if (!url) {
		return { ok: false, message: "Missing url", data: null };
	}

	return deleteSchema(
		"schemaOptimizerDeleteJobsByUrl",
		{ websiteId, url },
		"Failed to delete jobs for URL"
	);
}

export async function regenerateSchemaOptimizerJob(websiteId, jobId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, job: null };
	const parsed = await postSchema(
		"schemaOptimizerRegenerateJob",
		{ websiteId: schemaWebsiteId(websiteId), jobId: normalizeSchemaJobId(jobId) },
		"Failed to regenerate job"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, job: null };
	const job = normalizeSubmitJob(parsed.data);
	if (!job?.jobId) {
		return { ok: false, message: "No job id returned", job: null };
	}
	return { ok: true, message: parsed.message, job };
}

export async function previewSchemaOptimizerSitemap(websiteId, sitemapUrl) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, preview: null };
	const parsed = await postSchema(
		"schemaOptimizerSitemapPreview",
		{ websiteId: schemaWebsiteId(websiteId), sitemapUrl: String(sitemapUrl).trim() },
		"Failed to load sitemap"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, preview: null };
	return { ok: true, message: parsed.message, preview: normalizePreview(parsed.data) };
}

export async function previewSchemaOptimizerCsv(websiteId, csvText) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, preview: null };
	const parsed = await postSchema(
		"schemaOptimizerCsvPreview",
		{ websiteId: schemaWebsiteId(websiteId), csvText: String(csvText) },
		"Failed to parse CSV"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, preview: null };
	return { ok: true, message: parsed.message, preview: normalizePreview(parsed.data) };
}

export async function submitSchemaOptimizerRun(
	websiteId,
	{ source, urls, useBatchApi = true, sessionId } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, run: null };
	const parsed = await postSchema(
		"schemaOptimizerSubmitRun",
		{
			websiteId: schemaWebsiteId(websiteId),
			source,
			urls: Array.isArray(urls) ? urls : [],
			useBatchApi: Boolean(useBatchApi),
			...(sessionId ? { sessionId } : {}),
		},
		"Failed to start schema run"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, run: null };
	const run = normalizeSubmitRun(parsed.data);
	if (!run?.runId) {
		return { ok: false, message: "No run id returned", run: null };
	}
	return { ok: true, message: parsed.message, run };
}

export async function getSchemaOptimizerRun(websiteId, runId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, run: null };
	if (runId == null || runId === "") {
		return { ok: false, message: "Missing run id", run: null };
	}
	const parsed = await getSchema(
		"schemaOptimizerRun",
		{ websiteId, runId: Number(runId) },
		"Failed to load schema run"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, run: null };
	const run = normalizeRunDetail(parsed.data);
	if (!run?.id && runId != null) {
		run.id = Number(runId);
	}
	return { ok: true, message: parsed.message, run };
}

function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function pollSchemaOptimizerJob(
	websiteId,
	jobId,
	{ intervalMs = SCHEMA_OPTIMIZER_JOB_POLL_MS, onTick } = {}
) {
	for (;;) {
		const result = await getSchemaOptimizerJob(websiteId, jobId);
		if (!result.ok) return result;
		const job = result.job;
		if (typeof onTick === "function") onTick(job);
		const status = job?.status ?? job?.Status;
		if (isSchemaOptimizerJobFailed(status)) {
			return {
				ok: false,
				message: job?.error || job?.progressMessage || "Job failed",
				job,
			};
		}
		if (isSchemaOptimizerJobSuccess(status)) {
			return { ok: true, message: null, job };
		}
		await sleep(intervalMs);
	}
}
